<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import InboxesAPI from 'dashboard/api/inboxes';

export default {
  components: {
    SettingsSection,
    ImapSettings,
    SmtpSettings,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      socialProofEnabled: false,
      socialProofColor: '',
      socialProofDuration: 5,
      socialProofTestLocation: '',
      socialProofTestMessage: '',
      socialProofTriggering: false,
      socialContactPluginEnabled: false,
      socialContactRows: [{ kind: 'facebook', url: '' }],
      whatsAppInboxAPIKey: '',
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.socialProofEnabled = this.inbox.social_proof_enabled || false;
      this.socialProofColor = this.inbox.social_proof_color || '';
      this.socialProofDuration = this.inbox.social_proof_duration || 5;
      this.socialProofTestLocation = '';
      this.socialProofTestMessage = '';
      this.socialContactPluginEnabled =
        this.inbox.social_contact_plugin_enabled || false;
      const scl = this.inbox.social_contact_links;
      this.socialContactRows =
        Array.isArray(scl) && scl.length > 0
          ? scl.map(l => ({
              kind: l.kind || 'website',
              url: l.url || '',
            }))
          : [{ kind: 'facebook', url: '' }];
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    handleSocialProofFlag() {
      this.updateSocialProof();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateSocialProof() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            social_proof_enabled: this.socialProofEnabled,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateSocialProofAppearance() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            social_proof_color: this.socialProofColor || null,
            social_proof_duration: Number(this.socialProofDuration) || 5,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },

    handleSocialContactPluginFlag() {
      this.updateSocialContactPlugin({ linksToo: false });
    },
    addSocialContactRow() {
      this.socialContactRows.push({ kind: 'website', url: '' });
    },
    removeSocialContactRow(index) {
      if (this.socialContactRows.length <= 1) {
        return;
      }
      this.socialContactRows.splice(index, 1);
    },
    async updateSocialContactPlugin({ linksToo = true } = {}) {
      try {
        const links = linksToo
          ? this.socialContactRows
              .map(r => ({
                kind: r.kind,
                url: (r.url || '').trim(),
              }))
              .filter(r => r.url.length > 0)
          : undefined;
        const channel = {
          social_contact_plugin_enabled: this.socialContactPluginEnabled,
        };
        if (links !== undefined) {
          channel.social_contact_links = links;
        }
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel,
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async triggerSocialProofTest() {
      this.socialProofTriggering = true;
      try {
        await InboxesAPI.triggerSocialProof(this.inbox.id, {
          location: this.socialProofTestLocation.trim() || undefined,
          message: this.socialProofTestMessage.trim() || undefined,
        });
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_TEST_SUCCESS')
        );
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE');
        useAlert(message);
      } finally {
        this.socialProofTriggering = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>
  <div v-else-if="isALineChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_HEADING')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_SUB_HEAD')"
      >
        <woot-code
          :script="inbox.web_widget_script"
          lang="html"
          :codepen-title="`${inbox.name} - Chatwoot Widget Test`"
          enable-code-pen
        />
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      >
        <woot-code :script="inbox.hmac_token" />
        <template #subTitle>
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_LINK_TO_DOCS') }}
          </a>
        </template>
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
      >
        <div class="flex items-center gap-2">
          <input
            id="hmacMandatory"
            v-model="hmacMandatory"
            type="checkbox"
            @change="handleHmacFlag"
          />
          <label for="hmacMandatory">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
          </label>
        </div>
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_SUBTITLE')"
      >
        <div class="flex items-center gap-2">
          <input
            id="socialProofEnabled"
            v-model="socialProofEnabled"
            type="checkbox"
            @change="handleSocialProofFlag"
          />
          <label for="socialProofEnabled">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_ENABLE_LABEL') }}
          </label>
        </div>
      </SettingsSection>
      <SettingsSection
        v-if="socialProofEnabled"
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_APPEARANCE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_APPEARANCE_SUBTITLE')
        "
      >
        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-3">
            <label for="socialProofColor" class="w-32 text-sm">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_ACCENT_COLOR') }}
            </label>
            <input
              id="socialProofColor"
              v-model="socialProofColor"
              type="color"
              class="h-8 w-16 cursor-pointer rounded border border-gray-200"
            />
            <span class="text-sm text-gray-500">
              {{
                socialProofColor ||
                $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_DEFAULT_COLOR_HINT')
              }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <label for="socialProofDuration" class="w-32 text-sm">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_DISPLAY_DURATION')
              }}
            </label>
            <input
              id="socialProofDuration"
              v-model="socialProofDuration"
              type="number"
              min="1"
              max="60"
              class="w-20 rounded border border-gray-200 px-2 py-1 text-sm"
            />
            <span class="text-sm text-gray-500">{{
              $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_SECONDS')
            }}</span>
          </div>
          <woot-button size="small" @click="updateSocialProofAppearance">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_SAVE_APPEARANCE') }}
          </woot-button>
          <p class="mt-4 text-sm text-gray-600">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_TEST_DESCRIPTION') }}
          </p>
          <label
            for="socialProofTestMessage"
            class="mt-3 block text-sm font-medium text-gray-700 dark:text-slate-300"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_TEST_MESSAGE_LABEL')
            }}
          </label>
          <textarea
            id="socialProofTestMessage"
            v-model="socialProofTestMessage"
            rows="2"
            class="mt-1 w-full max-w-lg rounded border border-gray-200 px-2 py-1.5 text-sm"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_TEST_MESSAGE_PLACEHOLDER'
              )
            "
          />
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <input
              v-model="socialProofTestLocation"
              type="text"
              class="min-w-[12rem] flex-1 rounded border border-gray-200 px-2 py-1 text-sm"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_TEST_LOCATION_PLACEHOLDER'
                )
              "
            />
            <woot-button
              size="small"
              :is-loading="socialProofTriggering"
              :disabled="socialProofTriggering"
              @click="triggerSocialProofTest"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.SOCIAL_PROOF_TEST_BUTTON') }}
            </woot-button>
          </div>
        </div>
      </SettingsSection>
    </div>
  </div>
  <div v-else-if="isAPIInbox" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex items-center gap-2">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')"
      >
        <woot-code :script="inbox.forward_to_email" />
      </SettingsSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config" class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')"
      >
        <woot-code :script="inbox.provider_config.webhook_verify_token" />
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')"
      >
        <woot-code :script="inbox.provider_config.api_key" />
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
        "
      >
        <div
          class="flex items-center justify-between flex-1 mt-2 whatsapp-settings--content"
        >
          <woot-input
            v-model="whatsAppInboxAPIKey"
            type="text"
            class="flex-1 mr-2"
            :placeholder="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
              )
            "
          />
          <woot-button
            :disabled="v$.whatsAppInboxAPIKey.$invalid"
            @click="updateWhatsAppInboxAPIKey"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
          </woot-button>
        </div>
      </SettingsSection>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  ::v-deep input {
    margin-bottom: 0;
  }
}
</style>
